import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MusicService } from '../../core/services/music.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  createParallaxCity,
  ParallaxAPI,
  ParallaxOptions,
} from '../../shared/helpers/parallax-city';
import { environment } from '../../../environments/environment';

// ===== Helpers =====
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const rand = (min: number, max: number) => Math.random() * (max - min) + min;

class Player {
  x = 0;
  y = 0;
  r = 18; // ← bleibt dein runder Kollisions-Radius
  vy = 0;
  alive = true;
  rot = 0; // wird nicht mehr zum Rotieren benutzt (Pixel bleiben crisp)
  lastFlapTime = 0;

  // === Sprite-Handling (einmalig laden & wiederverwenden) ===
  private static sprite: HTMLImageElement | null = null;
  private static spriteReady = false;
  private static spriteURL = 'assets/img/player.png';
  // Pivot im Bild (0..1) – feinjustierbar, damit der Kopf/Cockpit mittig sitzt
  // 0,0 = links oben; 1,1 = rechts unten
  private static anchorX = 0.42;
  private static anchorY = 0.6;

  static SPRITE_DIAMETER_MULT = 2; // 0.9 = 90% vom Kreis-Durchmesser
  static MIN_PIXEL_HEIGHT = 24; // min. Zeichengröße, damit es nicht zu winzig wird

  static preload(url?: string) {
    if (url) Player.spriteURL = url;
    if (Player.spriteReady) return;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      Player.sprite = img;
      Player.spriteReady = true;
    };
    img.src = Player.spriteURL;
  }

  // Mobile-optimierte Größe
  private isMobile =
    /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ||
    'ontouchstart' in window ||
    window.innerWidth < 768;

  constructor(
    private getH: () => number,
    private getW: () => number,
    private getGroundH: () => number
  ) {
    Player.preload(); // sicherstellen, dass das Bild geladen wird
    this.reset();
  }

  reset() {
    this.x = this.getW() * 0.25;
    this.y = this.getH() * 0.5;

    const baseSize = Math.min(this.getW(), this.getH()) * 0.03 + 10;
    this.r = this.isMobile ? baseSize * 0.85 : baseSize; // 15% kleiner auf Mobile

    this.vy = 0;
    this.alive = true;
    this.rot = 0;
  }

  flap() {
    if (!this.alive) return;
    const now = performance.now();
    const dt = (now - this.lastFlapTime) / 1000;
    this.lastFlapTime = now;

    // Detect mobile for ONLY flap strength adjustment
    const isMobile =
      /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ||
      'ontouchstart' in window ||
      window.innerWidth < 768;

    // Base boost - keep desktop value, reduce only for mobile
    let boost = -Math.max(420, this.getH() * 0.55);

    // Mobile: reduce flap strength by 25% for better control
    if (isMobile) {
      boost *= 0.4;
    }

    // Keep the rapid-tap multiplier logic exactly the same
    if (dt < 0.25) {
      const multiplier = 1 + (0.25 - dt) * 5;
      boost *= multiplier;
    }

    this.vy = boost;
  }

  update(dt: number) {
    const G = Math.max(1400, this.getH() * 2.4);
    this.vy += G * dt;
    this.y += this.vy * dt;

    // kein Rotieren des Sprites (sonst wird Pixelart weichgezeichnet)
    // optional: leichtes Banking in Stufen (Skalierung statt Rotation) – bei Bedarf nachrüstbar

    if (this.y > this.getH() - this.getGroundH()) this.alive = false;
    if (this.y < 0) {
      this.y = 0;
      this.vy = 0;
    }
  }

  draw(ctx: CanvasRenderingContext2D) {
    const img = Player.sprite;

    if (!Player.spriteReady || !img) {
      // Fallback-Kreis
      ctx.save();
      ctx.fillStyle = '#34d399';
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    // Mobile-optimierte Sprite-Größe
    const sizeMult = this.isMobile ? 1.6 : Player.SPRITE_DIAMETER_MULT; // kleiner auf Mobile
    const minSize = this.isMobile ? 20 : Player.MIN_PIXEL_HEIGHT;

    const targetH = Math.max(minSize, Math.round(this.r * 2 * sizeMult));

    const scale = targetH / img.naturalHeight;
    const dw = Math.max(1, Math.round(img.naturalWidth * scale));
    const dh = Math.max(1, Math.round(img.naturalHeight * scale));

    const tilt = Math.max(-1, Math.min(1, this.vy / 900));
    const scaleX = tilt < -0.3 ? 0.97 : tilt > 0.3 ? 1.03 : 1.0;

    ctx.save();
    (ctx as CanvasRenderingContext2D & { imageSmoothingEnabled?: boolean }).imageSmoothingEnabled =
      false;
    ctx.translate(this.x, this.y);
    ctx.scale(scaleX, 1);

    const ax = Player.anchorX ?? 0.42;
    const ay = Player.anchorY ?? 0.6;

    ctx.drawImage(img, Math.round(-dw * ax), Math.round(-dh * ay), dw, dh);
    ctx.restore();
  }

  // === Runde Hitbox beibehalten ===
  getAABB() {
    // Bounding-Box des Kreises (wie bisher – leicht kompakter durch 0.8)
    return {
      x: this.x - this.r * 0.8,
      y: this.y - this.r * 0.8,
      w: this.r * 1.6,
      h: this.r * 1.6,
    };
  }

  destroy() {
    // nichts zu bereinigen – Sprite bleibt gecacht
  }

  static cleanup() {
    if (Player.sprite) {
      Player.sprite = null;
      Player.spriteReady = false;
    }
  }
}

// Neon-Palette wie im City-Background
const NEON_COLORS = ['#00ffff', '#ff00ff', '#ff0080', '#ffff00', '#00ff00', '#8b5cf6'];

// Score -> Farbindex (alle 20 Punkte, bis max. 120)
function colorForScore(score: number): string {
  const capped = Math.min(score, 120); // bei 120 bleibt sie stehen
  const step = Math.floor(capped / 20); // 0..6
  const idx = Math.min(step, NEON_COLORS.length - 1); // Palette clampen
  return NEON_COLORS[idx];
}

class Pipe {
  static lastCenterY = NaN; // begrenzt den vertikalen Sprung zwischen Spawns

  w = 0;
  gap = 0;
  top = 0;
  x = 0;
  passed = false;

  private colorAtSpawn = NEON_COLORS[0];
  private baseTop = 0; // Original-Position ohne Bewegung
  private moveTime = 0; // Zeit für Sinus-Bewegung
  private moveSpeed = 0; // Geschwindigkeit der Bewegung
  moveAmplitude = 0; // Stärke der Bewegung
  hasCoin = false;
  // Mobile-Detection
  private get isMobile(): boolean {
    return (
      /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ||
      'ontouchstart' in window ||
      window.innerWidth < 768
    );
  }

  // Mobile-spezifische Konstanten
  private get mobileScale(): number {
    return this.isMobile ? 0.7 : 1.0; // 30% kleiner auf Mobile
  }

  constructor(private comp: CypherFlyComponent, x: number) {
    this.reset(x);
  }

  private snap(v: number) {
    return Math.round(v);
  }

  reset(x: number) {
    const W = (this.comp as unknown as { W: number }).W;
    const H = (this.comp as unknown as { H: number }).H;
    const groundH = (this.comp as unknown as { ground: { h: number } }).ground.h;

    // Keep original pipe width
    this.w = Math.max(60, Math.min(120, W * 0.08));

    // Keep original speed calculation exactly the same
    const baseSpeed = Math.max(240, Math.min(280, W * 0.35));
    const maxExtra = 300;
    const rampTime = 60;
    const tEl = Math.min((this.comp as unknown as { time: number }).time || 0, rampTime);
    const factor = Math.log1p(tEl) / Math.log1p(rampTime);
    const speed = baseSpeed + maxExtra * factor;

    // Keep original difficulty progression
    const score = (this.comp as unknown as { score: number }).score || 0;
    const time = (this.comp as unknown as { time: number }).time || 0;
    const pTime = Math.min(time / 60, 1);
    const pScore = Math.min(score / 120, 1);
    let prog = Math.min(1, 0.55 * pTime + 0.45 * pScore);
    prog = Math.pow(prog, 1.3);

    // Keep original base gap calculation
    const baseGap0 = clamp(H * 0.3, 200, 340);
    let gap = baseGap0 * (1 - 0.25 * prog);

    // Keep original fairness calculation exactly the same
    const player = (this.comp as unknown as { player?: Player }).player;
    const px = player ? player.x : W * 0.25;
    const dist = Math.max(1, x - px);
    const tToPipe = dist / speed;
    const reach = clamp(H * 0.3 + tToPipe * H * 0.45, H * 0.36, H * 0.66);
    const playerY = player ? player.y : H * 0.5;

    const margin = 40;
    const minCenterHard = margin + gap / 2;
    const maxCenterHard = H - groundH - margin - gap / 2;

    let gy = rand(minCenterHard, maxCenterHard);
    const allowedMin = clamp(playerY - reach, minCenterHard, maxCenterHard);
    const allowedMax = clamp(playerY + reach, minCenterHard, maxCenterHard);
    gy = clamp(gy, allowedMin, allowedMax);

    const diff = Math.abs(gy - playerY);
    const widen = diff / Math.max(1, reach);
    gap += widen * clamp(H * 0.1, 60, 140) * this.mobileScale;

    // Keep original minimum gap
    const minGap = clamp(H * 0.26, 180, 320);
    gap = Math.max(minGap, gap);

    // *** ONLY MOBILE CHANGE: Make gap smaller on mobile ***
    const isMobile =
      /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ||
      'ontouchstart' in window ||
      window.innerWidth < 768;

    if (isMobile) {
      gap *= 0.65; // 15% smaller gap on mobile
    }

    this.gap = gap;

    // Keep original vertical jump limitation exactly the same
    const last = Number.isFinite(Pipe.lastCenterY) ? Pipe.lastCenterY : gy;
    const maxStep = Math.max(H * (0.22 - 0.04 * prog), H * 0.1);
    const minC = margin + this.gap / 2;
    const maxC = H - groundH - margin - this.gap / 2;

    if (gy > last + maxStep) gy = last + maxStep;
    if (gy < last - maxStep) gy = last - maxStep;
    gy = clamp(gy, minC, maxC);

    Pipe.lastCenterY = gy;

    this.top = gy - this.gap / 2;
    this.baseTop = this.top; // ← DAS war der fehlende Teil!
    this.x = x;
    this.passed = false;

    if (this.comp.movingPipesMode) {
      this.moveTime = Math.random() * Math.PI * 2; // Zufälliger Start-Offset
      this.moveSpeed = this.isMobile ? 3.5 : 2.5 + Math.random() * 1.5; // 1.5-2.5 Geschwindigkeit
      this.moveAmplitude = this.isMobile ? 25 : 40 + Math.random() * 25; // 15-40px Bewegung
    } else {
      this.moveSpeed = 0;
      this.moveAmplitude = 0;
    }

    this.colorAtSpawn = colorForScore(score);
  }

  update(dt: number, speed: number) {
    this.x -= speed * dt; // ruhige, lineare Bewegung

    if (this.moveAmplitude > 0) {
      this.moveTime += dt * this.moveSpeed;
      const offset = Math.sin(this.moveTime) * this.moveAmplitude;
      this.top = this.baseTop + offset;

      // Grenzen checken (nicht zu hoch/tief)
      const H = (this.comp as unknown as { H: number }).H;
      const groundH = (this.comp as unknown as { ground: { h: number } }).ground.h;
      const margin = 40;

      const minTop = margin;
      const maxTop = H - groundH - margin - this.gap;
      this.top = clamp(this.top, minTop, maxTop);
    }
  }

  offscreen() {
    return this.x + this.w < -10;
  }

  draw(ctx: CanvasRenderingContext2D, H: number, groundH: number) {
    const beam = 12;
    const x = this.snap(this.x);
    const ty = this.snap(this.top);
    const by = this.snap(ty + this.gap);
    const color = this.colorAtSpawn;

    // Kollision (unsichtbar)
    ctx.fillStyle = color;
    ctx.globalAlpha = 1;
    ctx.fillRect(x, 0, this.w, ty);
    ctx.fillRect(x, by, this.w, H - groundH - by);

    // Sichtbare Balken (stabil, ohne shadowBlur)
    ctx.fillStyle = color;
    ctx.fillRect(x - 6, ty - beam, this.w + 12, beam);
    ctx.fillRect(x - 6, by, this.w + 12, beam);

    // dezente Kante
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.fillRect(x - 6, ty - beam, this.w + 12, 2);
    ctx.fillRect(x - 6, by + beam - 2, this.w + 12, 2);
  }

  collides(box: { x: number; y: number; w: number; h: number }, H: number, groundH: number) {
    const topY = this.top,
      bottomY = topY + this.gap;
    const hitTop =
      box.x < this.x + this.w && box.x + box.w > this.x && box.y < topY && box.y + box.h > 0;
    const hitBottom =
      box.x < this.x + this.w &&
      box.x + box.w > this.x &&
      box.y < H - groundH &&
      box.y + box.h > bottomY;
    return hitTop || hitBottom;
  }
}

function loadPixelSVG(url: string, grid = 16, pad = 1, tint?: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      const w = img.naturalWidth || 256;
      const h = img.naturalHeight || 256;

      // Low-res Ziel
      const low = document.createElement('canvas');
      low.width = grid;
      low.height = grid;
      const lctx = low.getContext('2d');
      if (!lctx) return reject(new Error('2D context not available'));
      (
        lctx as CanvasRenderingContext2D & { imageSmoothingEnabled?: boolean }
      ).imageSmoothingEnabled = false;

      // In Grid einpassen (mit Padding), pixel-snapped
      const targetW = grid - pad * 2;
      const targetH = grid - pad * 2;
      const aspect = w / h;
      let dw = targetW,
        dh = targetH;
      if (aspect >= 1) {
        dh = Math.round(targetW / aspect);
      } else {
        dw = Math.round(targetH * aspect);
      }
      const dx = Math.round((grid - dw) / 2);
      const dy = Math.round((grid - dh) / 2);

      lctx.clearRect(0, 0, grid, grid);
      lctx.drawImage(img, dx, dy, dw, dh);

      // Optional einfärben in Neon-Farbe
      if (tint) {
        lctx.globalCompositeOperation = 'source-in';
        lctx.fillStyle = tint;
        lctx.fillRect(0, 0, grid, grid);
        lctx.globalCompositeOperation = 'source-over';
      }

      resolve(low);
    };
    img.onerror = reject;
    img.src = url;
  });
}

class LogoCoin {
  // etwas größer als vorher
  r = 18; // Kollisionradius
  t = 0;
  taken = false;

  // Pinker Sprite-Cache (einmal laden, dann weiterverwenden)
  private static spritePink: HTMLCanvasElement | null = null;
  private static loading: Promise<void> | null = null;
  private offsetY = 0;
  private attachedPipe: Pipe | null = null;

  constructor(public x: number, public y: number, pipe?: Pipe) {
    this.ensureSprite();
    if (pipe) {
      this.attachedPipe = pipe;
      this.offsetY = y - (pipe.top + pipe.gap / 2);
    }
  }

  private ensureSprite() {
    if (LogoCoin.spritePink || LogoCoin.loading) return;
    // IMMER pink (#ff00ff), klein gerendert (16×16), dann hochskaliert
    LogoCoin.loading = loadPixelSVG('assets/img/proton.svg', 16, 1, '#ff00ff')
      .then((canvas) => {
        LogoCoin.spritePink = canvas;
      })
      .catch(() => {
        LogoCoin.spritePink = null;
      })
      .finally(() => {
        LogoCoin.loading = null;
      }) as unknown as Promise<void>;
  }

  update(dt: number, speed: number) {
    this.x -= speed * dt;
    this.t += dt;

    if (this.attachedPipe) {
      this.y = this.attachedPipe.top + this.attachedPipe.gap / 2 + this.offsetY;
    }
  }

  draw(ctx: CanvasRenderingContext2D) {
    const spr = LogoCoin.spritePink;

    // Fallback: einfacher pinker Kreis, falls Sprite noch lädt
    if (!spr) {
      ctx.save();
      ctx.fillStyle = '#ff00ff';
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    // 8-bit Flip (kein Rotation-Matsch)
    const flipTable = [1.0, 0.9, 0.8, 0.9]; // kurz & ruhig
    const idx = Math.floor(this.t * 6) % flipTable.length;
    const flipX = flipTable[idx];

    // Größer skalieren, aber crisp (Integer-Scale)
    const base = spr.width; // 16
    const scale = 3; // 16 * 3 = 48px – schön groß & pixelig
    const dw = base * scale;
    const dh = base * scale;

    ctx.save();
    (ctx as CanvasRenderingContext2D & { imageSmoothingEnabled?: boolean }).imageSmoothingEnabled =
      false;
    ctx.translate(this.x, this.y);
    ctx.scale(flipX, 1);

    // PIXEL-GLOW (rechteckig, stabil, ohne Blur)
    ctx.globalAlpha = 0.25;
    ctx.fillStyle = '#ff00ff';
    // kleiner „Halo“-Rahmen um den Sprite-Bereich
    ctx.fillRect(-dw / 2 - 2, -dh / 2 - 2, dw + 4, dh + 4);
    ctx.globalAlpha = 1;

    // Sprite selbst
    ctx.drawImage(spr, -dw / 2, -dh / 2, dw, dh);

    // dünne helle Kante (Pixel-Schimmer)
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillRect(-dw / 2 - 2, -dh / 2 - 2, dw + 4, 2); // oben
    ctx.fillRect(-dw / 2 - 2, dh / 2, dw + 4, 2); // unten
    ctx.fillRect(-dw / 2 - 2, -dh / 2 - 2, 2, dh + 4); // links
    ctx.fillRect(dw / 2, -dh / 2 - 2, 2, dh + 4); // rechts

    ctx.restore();
  }

  collides(box: { x: number; y: number; w: number; h: number }) {
    // Kreis vs. AABB
    const cx = Math.max(box.x, Math.min(this.x, box.x + box.w));
    const cy = Math.max(box.y, Math.min(this.y, box.y + box.h));
    const dx = this.x - cx,
      dy = this.y - cy;
    return dx * dx + dy * dy <= this.r * this.r;
  }

  static cleanup() {
    if (LogoCoin.spritePink) {
      LogoCoin.spritePink = null;
      LogoCoin.loading = null;
    }
  }
}

class Coin {
  r = 10;
  t = 0;
  taken = false;

  constructor(public x: number, public y: number) {}

  update(dt: number, speed: number) {
    this.x -= speed * dt;
    this.t += dt;
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(Math.sin(this.t * 6) * 0.35);
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.arc(0, 0, this.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#78350f';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, this.r * 0.65, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  collides(box: { x: number; y: number; w: number; h: number }) {
    const cx = clamp(this.x, box.x, box.x + box.w);
    const cy = clamp(this.y, box.y, box.y + box.h);
    const dx = this.x - cx,
      dy = this.y - cy;
    return dx * dx + dy * dy <= this.r * this.r;
  }
}

@Component({
  selector: 'app-cypher-fly',
  standalone: true,
  templateUrl: './cypher-fly.component.html',
  styleUrl: './cypher-fly.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CypherFlyComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gameCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('bgCanvas', { static: true }) bgRef!: ElementRef<HTMLCanvasElement>;
  private bg!: ParallaxAPI;

  get isMobile(): boolean {
    return (
      /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ||
      'ontouchstart' in window ||
      window.innerWidth < 768
    );
  }

  private get mobileScale(): number {
    return this.isMobile ? 0.7 : 1.0; // 30% kleiner auf Mobile
  }

  private readonly musicService = inject(MusicService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  // UI/State
  score = 0;
  best = 0;
  state: 'MENU' | 'PLAY' | 'PAUSE' | 'OVER' = 'MENU';
  muted = false;
  fastMode = false;
  movingPipesMode = false;
  centerHtml: SafeHtml | '' = '';
  elapsedLabel = '0:00';
  fps = 60;
  isDev = !environment.production;
  // Internal state
  private elapsedMs = 0;
  private elapsedSec = 0;
  private frameCount = 0;
  private lastFpsTime = 0;
  private lastScoreSec = -1;
  private timeScore = 0;
  private passScore = 0;
  private coinScore = 0;
  private lastUIUpdate = 0;
  private scoreChanged = false;

  // Canvas & loop
  private ctx!: CanvasRenderingContext2D;
  private W = 0;
  private H = 0;
  private rafId = 0;
  private last = performance.now();
  private time = 0;
  // Orientation state: true when device is vertical (portrait)
  rotateNotice = false;

  // Event listeners
  private eventListeners: Array<{
    element: EventTarget;
    event: string;
    handler: EventListenerOrEventListenerObject;
  }> = [];

  // World
  private player?: Player;
  private pipes: Pipe[] = [];
  private coins: Coin[] = [];
  private ground = {
    h: 0,
    draw: () => {
      // no-op to satisfy interface, avoid empty function lint
      return;
    },
  };

  ngAfterViewInit() {
    this.best = Number(localStorage.getItem('floppy_best') || 0);
    Player.preload('assets/img/player.png'); // falls du einen anderen Pfad nutzt

    // === Canvas-Kontexte ===
    const gameCanvas = this.canvasRef.nativeElement;
    // alpha: true, damit Parallax darunter sichtbar ist
    const ctx = gameCanvas.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('2D context not available');
    this.ctx = ctx;

    // Parallax auf dem **HINTERGRUND-CANVAS**
    const bgCanvas = this.bgRef.nativeElement;

    // leichte Reduktion der Effekte auf High-DPR (Mobile)
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    this.bg = createParallaxCity(bgCanvas, {
      speed: 8,
      words: ['CYPHERGANG'],
      rainCount: isMobile ? 20 : 40,
      enableLightning: true,
      showCRT: !isMobile, // CRT weglassen auf Mobile spart Zeit
    } as Partial<ParallaxOptions>);

    this.resize(); // setzt Größen + DPR

    // Resize beide Canvases aktuell halten
    const ro = new ResizeObserver(() => this.resize());
    ro.observe(gameCanvas.parentElement ? gameCanvas.parentElement : gameCanvas);

    // UI initial
    setTimeout(() => {
      this.showMenu();
      this.cdr.markForCheck();
    }, 0);

    // === Game Loop ===
    this.ngZone.runOutsideAngular(() => {
      const loop = (now: number) => {
        const dt = Math.max(0, Math.min(0.05, (now - this.last) / 1000)); // clamp dt
        this.last = now;
        this.time += dt;

        // 1) Logik
        this.update(dt);

        // 2) Render: erst Hintergrund, dann Spiel (beide 1x!)
        this.bg.tick(dt);
        this.draw();

        // 3) UI-Throttle
        if (this.scoreChanged && now - this.lastUIUpdate > 100) {
          this.lastUIUpdate = now;
          this.scoreChanged = false;
          if (!this.fastMode && this.score > 120) {
            this.fastMode = true;
            this.musicService.startBackgroundMusic('assets/audio/faster_8_Bit_Menu.mp3');
          }
          this.ngZone.run(() => this.cdr.markForCheck());
        }
        this.rafId = requestAnimationFrame(loop);
      };
      this.rafId = requestAnimationFrame(loop);
    });

    // Touch-Events auf das gesamte Container-Element, nicht nur Canvas
    const container = this.canvasRef.nativeElement.parentElement;

    if (container) {
      // Alle Touch-Events auf Container-Level abfangen
      this.addEventListener(container, 'touchstart', this.onTouchStart);
      this.addEventListener(container, 'touchend', this.onTouchEnd);
      this.addEventListener(container, 'touchmove', this.onTouchMove);

      // Pointer-Events auch auf Container
      this.addEventListener(container, 'pointerdown', this.onPointerDown);

      // Für Desktop: auch click-Events
      this.addEventListener(container, 'click', this.onContainerClick);
    }
    // Window-Events für Keyboard
    this.addEventListener(window, 'keydown', this.onWindowKeydown);
  }

  ngOnDestroy() {
    this.cleanup();
  }

  // Touch-Handlers
  private onTouchStart = (e: TouchEvent) => {
    e.preventDefault(); // Verhindert Scroll, Zoom, etc.
    this.handleInput();
  };

  private onTouchEnd = (e: TouchEvent) => {
    e.preventDefault();
  };

  private onTouchMove = (e: TouchEvent) => {
    e.preventDefault(); // Verhindert Scrollen während dem Spiel
  };

  // Pointer-Handler (funktioniert für Touch + Maus)
  private onPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    // Nur auf primäre Touches reagieren (erster Finger)
    if (e.isPrimary) {
      this.handleInput();
    }
  };

  // Click-Handler für Desktop
  private onContainerClick = (e: MouseEvent) => {
    e.preventDefault();
    this.handleInput();
  };

  private handleInput() {
    // Direkt flappen, egal in welchem State
    this.flap();
  }

  // ===== Event-Tools =====
  private addEventListener<K extends keyof HTMLElementEventMap>(
    element: HTMLElement,
    event: K,
    handler: (ev: HTMLElementEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void;
  private addEventListener<K extends keyof WindowEventMap>(
    element: Window,
    event: K,
    handler: (ev: WindowEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void;
  private addEventListener(
    element: EventTarget,
    event: string,
    handler: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ): void {
    element.addEventListener(event, handler as EventListener, options);
    this.eventListeners.push({ element, event, handler });
  }

  private removeAllEventListeners() {
    for (const l of this.eventListeners) l.element.removeEventListener(l.event, l.handler);
    this.eventListeners = [];
  }

  private cleanup() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.musicService.stopBackgroundMusic();
    this.removeAllEventListeners();
    Player.cleanup();
    LogoCoin.cleanup();
    this.player?.destroy();
    this.player = undefined;
    this.pipes.length = 0;
    this.coins.length = 0;
    this.centerHtml = '';
    this.bg?.destroy?.();
  }

  // ===== UI =====
  private trust(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private setCenter(html: string) {
    this.centerHtml = this.trust(html);
    this.cdr.markForCheck();
  }

  private showMenu() {
    this.setCenter(`
      <div class="panel">
        <h1 class="title">Cypher Fly</h1>
        <p class="subtitle">Fly through the gaps and get the Highscore.</p>
        <div style="display:flex; gap:10px; justify-content:center; flex-wrap:wrap;">
          <button class="btn" id="btnStart">▶️ Start</button>
        </div>
      </div>
    `);

    requestAnimationFrame(() => {
      const root = this.canvasRef.nativeElement.parentElement as HTMLElement;
      root
        .querySelector('#btnStart')
        ?.addEventListener('click', () => this.startGame(), { once: true });
      root.querySelector('#btnHow')?.addEventListener(
        'click',
        () => {
          this.setCenter(`
          <div class='panel'>
            <h2 class='title'>So spielst du</h2>
            <p>Tippe/Klicke oder drücke die <b>Leertaste</b>, um zu flappen.
            Weiche den Pfeilern aus, sammle Münzen (+5).
            <b>P</b> pausiert, <b>R</b> startet neu.</p>
            <div style='margin-top:14px;'><button class='btn' id='btnGo'>Los geht's</button></div>
          </div>
        `);
          requestAnimationFrame(() => {
            root
              .querySelector('#btnGo')
              ?.addEventListener('click', () => this.startGame(), { once: true });
          });
        },
        { once: true }
      );
    });
  }

  // Input
  private onWindowKeydown = (e: KeyboardEvent) => {
    if (e.code === 'Space') {
      e.preventDefault();
      this.flap();
    }
    if (e.key === 'p' || e.key === 'P' || e.key === 'Escape') this.togglePause();
    if (e.key === 'r' || e.key === 'R') this.restartGame();
    if (e.key === 'm' || e.key === 'M') this.toggleMute();
  };

  togglePause() {
    if (this.state === 'PLAY') {
      this.state = 'PAUSE';
      this.setCenter(
        `<div class='panel'><h2 class='title'>Pausiert</h2><p class='subtitle'>P/Esc: weiter</p></div>`
      );
    } else if (this.state === 'PAUSE') {
      this.state = 'PLAY';
      this.setCenter('');
    }
  }

  toggleMute() {
    this.muted = !this.muted;
    if (this.muted) this.musicService.stopBackgroundMusic();
    else if (this.state === 'PLAY')
      this.musicService.startBackgroundMusic('assets/audio/slow_8_Bit_Menu.mp3');
    this.cdr.markForCheck();
  }

  private restartGame() {
    if (['PLAY', 'PAUSE', 'OVER'].includes(this.state)) this.startGame();
  }

  private startGame() {
    this.player?.destroy();
    if (!this.muted) this.musicService.startBackgroundMusic('assets/audio/slow_8_Bit_Menu.mp3');
    this.resetWorld();
    this.state = 'PLAY';
    this.setCenter('');
  }

  private gameOver() {
    this.state = 'OVER';
    this.musicService.stopBackgroundMusic();
    this.best = Math.max(this.best, this.score);
    localStorage.setItem('floppy_best', String(this.best));
    this.setCenter(`
      <div class='panel'>
        <h2 class='title'>Game Over</h2>
        <p class='subtitle'>Score: <b>${this.score}</b> · Best: <b>${this.best}</b></p>
        <button class='btn' id='btnRestart'>🔁 Try again</button>
      </div>
    `);
    requestAnimationFrame(() => {
      const root = this.canvasRef.nativeElement.parentElement as HTMLElement;
      root
        .querySelector('#btnRestart')
        ?.addEventListener('click', () => this.startGame(), { once: true });
    });
  }

  // ===== Resize (beide Canvases!) =====
  @HostListener('window:resize') resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2) * this.mobileScale; // Deckel auf 2
    const w = (this.W = Math.floor(window.innerWidth));
    const h = (this.H = Math.floor(window.innerHeight));

    // Update orientation flag: show notice when portrait (vertical)
    this.rotateNotice = h > w;

    // Game-Canvas
    const gc = this.canvasRef.nativeElement;
    gc.style.width = w + 'px';
    gc.style.height = h + 'px';
    gc.width = Math.floor(w * dpr);
    gc.height = Math.floor(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // BG-Canvas skaliert sich per CSS, das Parallax-Modul nutzt clientWidth/Height
    const bc = this.bgRef.nativeElement;
    bc.style.width = w + 'px';
    bc.style.height = h + 'px';

    // Parallax intern informieren (reinitialisiert seine size-gebundenen Strukturen)
    this.bg?.resize();

    // Spielergröße neu berechnen
    this.player?.destroy();
    this.player?.reset();
  }

  // ===== Scoring/Time =====
  private formatElapsedSec(totalSec: number): string {
    const m = Math.floor(totalSec / 60),
      s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  private scoreFromSeconds(sec: number): number {
    return sec <= 60 ? sec : 60 + (sec - 60) * 2;
  }

  private resetWorld() {
    this.elapsedMs = 0;
    this.elapsedSec = 0;
    this.elapsedLabel = '0:00';
    this.lastScoreSec = -1;
    this.timeScore = 0;
    this.passScore = 0;
    this.coinScore = 0;
    this.score = 0;
    this.scoreChanged = true;
    this.fastMode = false;
    this.movingPipesMode = false;
    this.pipes.length = 0;
    this.coins.length = 0;
    this.player = new Player(
      () => this.H,
      () => this.W,
      () => this.ground.h
    );

    let x = this.W * 1.1;
    for (let i = 0; i < 5; i++) {
      this.pipes.push(new Pipe(this, x));
      x += this.gapDistance();
    }
    this.time = 0;
  }

  private gapDistance() {
    const base = clamp(
      this.W * (this.isMobile ? 0.42 : 0.36),
      this.isMobile ? 350 : 300,
      this.isMobile ? 580 : 520
    );
    const pTime = Math.min(this.time / 60, 1);
    const pScore = Math.min(this.score / 120, 1);
    const prog = Math.pow(Math.min(1, 0.5 * pTime + 0.5 * pScore), 1.2);
    const shrink = (this.isMobile ? 0.1 : 0.12) * prog; // Mobile weniger dicht
    return (
      base * (1 - shrink) + (Math.random() * (this.isMobile ? 60 : 80) - (this.isMobile ? 30 : 40))
    );
  }

  private flap() {
    if (this.state === 'MENU' || this.state === 'OVER') {
      this.startGame();
      return;
    }
    if (this.state === 'PAUSE') {
      this.togglePause();
      return;
    }
    this.player?.flap();
  }

  // ===== Update/Draw =====
  private update(dt: number) {
    if (this.state !== 'PLAY') return;

    if (this.isDev) {
      this.frameCount++;
      const now = performance.now();
      if (now - this.lastFpsTime >= 1000) {
        this.fps = Math.round((this.frameCount * 1000) / (now - this.lastFpsTime));
        this.frameCount = 0;
        this.lastFpsTime = now;

        // Log performance warnings
        if (this.fps < 45) {
          console.warn(`Low FPS detected: ${this.fps}`);
        }
      }
    }

    this.elapsedMs += dt * 1000;
    const sec = Math.floor(this.elapsedMs / 1000);

    if (sec !== this.lastScoreSec) {
      this.lastScoreSec = sec;
      this.elapsedSec = sec;
      const newLabel = this.formatElapsedSec(sec);
      const newTimeScore = this.scoreFromSeconds(sec);
      if (newLabel !== this.elapsedLabel || newTimeScore !== this.timeScore) {
        this.elapsedLabel = newLabel;
        this.timeScore = newTimeScore;
        this.score = this.timeScore + this.passScore + this.coinScore;
        this.scoreChanged = true;
      }
    }

    const baseSpeed = clamp(this.W * 0.35, 240, 280);
    const maxExtra = 300;
    const rampTime = 60;
    const t = Math.min(this.time, rampTime);
    const factor = Math.log1p(t) / Math.log1p(rampTime);
    const speed = baseSpeed + maxExtra * factor;

    this.player?.update(dt);

    if (!this.movingPipesMode && this.score >= 240) {
      this.movingPipesMode = true;
      console.log('🔥 MOVING PIPES ACTIVATED! 🔥'); // Debug
    }

    // Pipes
    let i = this.pipes.length;
    while (i--) {
      this.pipes[i].update(dt, speed);
      if (this.pipes[i].offscreen()) this.pipes.splice(i, 1);
    }
    if (this.pipes.length > 0) {
      const lastX = this.pipes[this.pipes.length - 1].x;
      if (lastX < this.W - this.gapDistance() * 0.6) this.spawnAfter(lastX);
    } else this.spawnAfter(this.W);

    // Coins
    i = this.coins.length;
    while (i--) {
      this.coins[i].update(dt, speed * 1.02);
      if (this.coins[i].x < -30) this.coins.splice(i, 1);
    }

    // Collisions
    const box = this.player?.getAABB();
    if (box && this.player) {
      for (const p of this.pipes) {
        if (!p.passed && p.x + p.w < this.player.x) {
          p.passed = true;
          this.passScore += 1;
          this.score = this.timeScore + this.passScore + this.coinScore;
          this.scoreChanged = true;
        }
        if (p.collides(box, this.H, this.ground.h)) this.player.alive = false;
      }
      for (const c of this.coins) {
        if (!c.taken && c.collides(box)) {
          c.taken = true;
          this.coinScore += 5;
          this.score = this.timeScore + this.passScore + this.coinScore;
          this.scoreChanged = true;
        }
      }
    }
    if (this.player && !this.player.alive) this.gameOver();
  }

  private spawnAfter(lastX: number) {
    const x = lastX + this.gapDistance();
    const p = new Pipe(this, x);
    this.pipes.push(p);

    if (Math.random() < 0.4 && this.coins.length < 10) {
      const coinY = p.top + p.gap * 0.5;
      this.coins.push(new LogoCoin(x + p.w / 2, coinY, p)); // ← Pipe mitgeben!
      p.hasCoin = true;

      if (this.score >= 100 && p.moveAmplitude > 0) {
        p.moveAmplitude = Math.min(p.moveAmplitude, 10 + Math.random() * 10);
      }
    }
  }

  private draw() {
    if (!this.ctx) return;

    // WICHTIG: Game-Canvas sauber wischen (transparent lassen!)
    this.ctx.clearRect(0, 0, this.W, this.H);

    // Sichtbare Pipes
    for (const p of this.pipes) {
      if (p.x < this.W && p.x + p.w > 0) p.draw(this.ctx, this.H, this.ground.h);
    }
    // Sichtbare Coins
    for (const c of this.coins) {
      if (!c.taken && c.x > -30 && c.x < this.W + 30) c.draw(this.ctx);
    }

    this.player?.draw(this.ctx);
    this.ground.draw();
  }
}
